<template>
  <div class="select-card">
    <div class="select-card-header">
      <div class="sch-left">
        <h2>Select an App to connect</h2>
        <p>
          Choose the App under your merchant account to link to this {{ platformLabel }} store.
          The App ID and Secret Key will be automatically passed to {{ platformLabel }} after you confirm.
        </p>
      </div>
      <div class="sch-right">
        <div class="mid-label">Merchant ID</div>
        <div class="mid-val">{{ merchantId || '—' }}</div>
      </div>
    </div>

    <app-table
      :apps="apps"
      :selected-app-id="selectedAppId"
      :loading="loading"
      :regions="regions"
      @select="$emit('select', $event)"
    />

    <div class="select-card-footer">
      <div class="footer-hint">
        <span v-if="selectedApp" class="footer-selected">
          Selected: <strong>{{ selectedApp.app_name }}</strong>
          <span class="app-id-hint">{{ selectedApp.app_id }}</span>
        </span>
        <span v-else class="footer-empty">
          No app selected — click a row above to select
        </span>
      </div>
      <button
        class="btn-green"
        :disabled="!canConfirm || confirming"
        @click="$emit('confirm')"
      >
        {{ confirming ? 'Confirming...' : 'Confirm Selection' }}
        <svg v-if="!confirming" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
          <path d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import AppTable from './AppTable.vue'

export default {
  name: 'ShoplazzaSelectAppCard',
  components: {
    AppTable,
  },
  props: {
    merchantId: {
      type: String,
      default: '',
    },
    apps: {
      type: Array,
      default: () => [],
    },
    selectedApp: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    confirming: {
      type: Boolean,
      default: false,
    },
    regions: {
      type: Array,
      default: () => [],
    },
    platformLabel: {
      type: String,
      default: '',
    },
  },
  computed: {
    selectedAppId () {
      return this.selectedApp ? this.selectedApp.app_id : ''
    },
    canConfirm () {
      return this.selectedApp && this.selectedApp.status !== 0
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.select-card {
  background: $white;
  border: 1px solid $border-light;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.06), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.select-card-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, $slate-dark 0%, $slate-mid 100%);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.sch-left {
  h2 {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 700;
    color: #fff;
  }

  p {
    margin: 0;
    max-width: 512px;
    font-size: 12px;
    line-height: 1.6;
    color: #94a3b8;
  }
}

.sch-right {
  flex-shrink: 0;
  padding: 10px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  text-align: right;
}

.mid-label {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
}

.mid-val {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 700;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: #fff;
}

.select-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  background: rgba(249, 250, 251, 0.8);
  border-top: 1px solid $border-light;
  flex-wrap: wrap;
}

.footer-hint {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: $ink-3;
}

.footer-empty {
  color: $ink-4;
}

.footer-selected {
  strong {
    color: $ink;
    font-weight: 600;
  }
}

.app-id-hint {
  margin-left: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: $ink-4;
}

.btn-green {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, $green, $green-dark);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
</style>
